import type { RefObject } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import {
  OtpCodeInput,
  type OtpCodeInputHandle,
} from "Components/Shared/Inputs/OtpCodeInput";
import type { PhoneNumber } from "Infrastructure/Api/Api";
import { useResource } from "Translations/Resources";
import { formatPhone } from "Utils/PhoneUtils";
import {
  RESOURCES,
  RESEND_COOLDOWN_SECONDS,
  RESEND_LINK_DELAY_SECONDS,
} from "./constants";

type Props = {
  submittedPhone: PhoneNumber;
  otpRef: RefObject<OtpCodeInputHandle | null>;
  isSending: boolean;
  isVerifying: boolean;
  isCodeComplete: boolean;
  secondsLeft: number;
  isOnCooldown: boolean;
  onCodeChange: (value: string) => void;
  onVerify: () => void;
  onResend: () => void;
  onBackToPhone: () => void;
  secondaryAction: React.ReactNode;
};

export const CodeStep = ({
  submittedPhone,
  otpRef,
  isSending,
  isVerifying,
  isCodeComplete,
  secondsLeft,
  isOnCooldown,
  onCodeChange,
  onVerify,
  onResend,
  onBackToPhone,
  secondaryAction,
}: Props) => {
  const { t } = useResource();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onVerify();
      }}
    >
      <DialogTitle>{t(RESOURCES.Title)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t(RESOURCES.CodeSent, { phone: formatPhone(submittedPhone) })}
        </Typography>
        <OtpCodeInput ref={otpRef} onChange={onCodeChange} />
        <Box mt={2} display="flex" justifyContent="space-between">
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onBackToPhone}
          >
            {t(RESOURCES.EditPhone)}
          </Link>
          {secondsLeft <=
            RESEND_COOLDOWN_SECONDS - RESEND_LINK_DELAY_SECONDS && (
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={onResend}
              sx={{
                pointerEvents: isSending || isOnCooldown ? "none" : "auto",
                opacity: isSending || isOnCooldown ? 0.5 : 1,
              }}
            >
              {isOnCooldown
                ? `${t(RESOURCES.ResendCode)} (${secondsLeft}s)`
                : t(RESOURCES.ResendCode)}
            </Link>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {secondaryAction}
        <Button
          type="submit"
          variant="contained"
          disabled={!isCodeComplete || isVerifying}
        >
          {t(RESOURCES.Verify)}
        </Button>
      </DialogActions>
    </form>
  );
};
